'use client';

import type { SettingPageTabs } from '@/app/(authenticated)/(dashboard)/settings/constants';
import type {
  AppShellSidebarNavGroup,
  AppShellSidebarNavItem,
} from '@/components/layout/dashboard/app-shell-sidebar-nav.types';
import { SETTINGS_SIDEBAR_GROUPS } from '@/domains/settings/settings-sidebar.config';
import { useAuthContext } from '@/hooks/use-auth';
import { useEnvironmentContext } from '@/providers/environment.provider';
import { useMemo } from 'react';

function isSettingsSectionVisible(
  section: SettingPageTabs,
  {
    isAdmin,
    enableInteractionsDetailsAccessControl,
    interactionsDetailsAccessControlMode,
    enableRemoteAccess,
  }: {
    isAdmin: boolean;
    enableInteractionsDetailsAccessControl: boolean;
    interactionsDetailsAccessControlMode: 'reason' | 'disabled';
    enableRemoteAccess: boolean;
  },
): boolean {
  switch (section) {
    case 'members':
    case 'organizations':
    case 'shared_pages':
    case 'filters':
    case 'integration':
      return isAdmin;
    case 'interactions_access':
      return enableInteractionsDetailsAccessControl && interactionsDetailsAccessControlMode === 'reason';
    case 'remote_access':
      return isAdmin && enableRemoteAccess;
    case 'projects':
    case 'alert_settings':
    case 'account':
      return true;
    default:
      return false;
  }
}

export function useSettingsSidebarItems(): AppShellSidebarNavItem<SettingPageTabs>[] {
  return useSettingsSidebarGroups().flatMap((group) => group.items);
}

export function useSettingsSidebarGroups(): AppShellSidebarNavGroup<SettingPageTabs>[] {
  const { isNebulyAdmin, isCompanyAdmin } = useAuthContext();
  const isAdmin = isCompanyAdmin || isNebulyAdmin;
  const { enableInteractionsDetailsAccessControl, interactionsDetailsAccessControlMode, enableRemoteAccess } =
    useEnvironmentContext();

  return useMemo(
    () =>
      SETTINGS_SIDEBAR_GROUPS.map((group) => ({
        ...group,
        items: group.items.filter((item) =>
          isSettingsSectionVisible(item.id, {
            isAdmin,
            enableInteractionsDetailsAccessControl,
            interactionsDetailsAccessControlMode,
            enableRemoteAccess,
          }),
        ),
      })).filter((group) => group.items.length > 0),
    [
      enableInteractionsDetailsAccessControl,
      enableRemoteAccess,
      interactionsDetailsAccessControlMode,
      isAdmin,
    ],
  );
}

export function useDefaultSettingsSection(): SettingPageTabs {
  const { isNebulyAdmin, isCompanyAdmin } = useAuthContext();
  const isAdmin = isCompanyAdmin || isNebulyAdmin;
  const items = useSettingsSidebarItems();

  if (items.length === 0) {
    return isAdmin ? 'members' : 'projects';
  }

  const preferredSection: SettingPageTabs = isAdmin ? 'members' : 'projects';
  if (items.some((item) => item.id === preferredSection)) {
    return preferredSection;
  }

  return items[0]!.id;
}
